import { useEffect, useRef } from 'react'
import { estimateShownAppraisal, trackEvent } from '@/helpers/tracking'
import { useAppraisalFlow } from './useAppraisalFlow'

/**
 * GA4 funnel events for the AI appraisal flow. Fired from an effect with
 * ref-based dedupe (mirrors the catalogue ConfiguratorSummary pattern) so
 * re-renders and StrictMode double-invocations never duplicate events.
 */
export function useAppraisalTracking(
  flow: ReturnType<typeof useAppraisalFlow>,
) {
  const lastQuestionsContext = useRef<string | null>(null)
  const lastEstimate = useRef<string | null>(null)
  const lastRejection = useRef<string | null>(null)
  const lastHandoff = useRef<string | null>(null)
  const lastRequest = useRef<string | null>(null)
  const errorTracked = useRef(false)

  const {
    phase,
    valuation,
    handoff,
    request,
    rejectionReason,
    rounds,
    context,
  } = flow
  const questionCount = flow.questions.length

  useEffect(() => {
    if (phase !== 'error') {
      // a retry that fails again should be tracked again
      errorTracked.current = false
    }
    if (phase === 'landing') {
      // reset() starts a fresh funnel — a rerun may legitimately repeat events
      lastQuestionsContext.current = null
      lastEstimate.current = null
      lastRejection.current = null
      lastHandoff.current = null
      lastRequest.current = null
    }

    switch (phase) {
      case 'questions': {
        if (!context || lastQuestionsContext.current === context) return
        lastQuestionsContext.current = context
        trackEvent('appraisal_questions_shown', {
          round: rounds,
          question_count: questionCount,
        })
        return
      }
      case 'result': {
        if (!valuation) return
        const estimateKey = `${valuation.payoutAmount}:${valuation.path}`
        if (lastEstimate.current === estimateKey) return
        lastEstimate.current = estimateKey
        estimateShownAppraisal({
          productName: valuation.productName,
          appraisalPath: valuation.path,
          payoutAmount: valuation.payoutAmount,
          status: valuation.payoutAmount > 0 ? 'ok' : 'no_payout',
          isAiGenerated: valuation.isAiGenerated,
          rounds,
        })
        return
      }
      case 'rejected': {
        if (!rejectionReason || lastRejection.current === rejectionReason) {
          return
        }
        lastRejection.current = rejectionReason
        trackEvent('appraisal_rejected', { reason: rejectionReason })
        return
      }
      case 'handoff': {
        if (!handoff || lastHandoff.current === handoff.target) return
        lastHandoff.current = handoff.target
        trackEvent('appraisal_handoff', { target: handoff.target })
        return
      }
      case 'request': {
        if (!request || lastRequest.current === request.productName) return
        lastRequest.current = request.productName
        trackEvent('appraisal_request_shown', { appraisal_path: request.path })
        return
      }
      case 'error': {
        if (errorTracked.current) return
        errorTracked.current = true
        trackEvent('appraisal_error')
        return
      }
    }
  }, [
    phase,
    valuation,
    handoff,
    request,
    rejectionReason,
    rounds,
    context,
    questionCount,
  ])
}
